import { useOrganizationLanguages } from 'api/languages/queries/useOrganizationLanguages'
import { usePurpose } from 'api/purposes/queries/usePurpose'
import { usePurposeVersion } from 'api/purposes/queries/usePurposeVersion'
import { usePurposeVersions } from 'api/purposes/queries/usePurposeVersions'
import { MaybeNull } from 'interfaces'
import { LanguageWithStatusDTO } from 'interfaces/languages/language'
import { PurposeDTO } from 'interfaces/purposes/purpose'
import React, { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryParams } from 'utils/hooks'
import { createCustomContext } from 'utils/hooks/createCustomContext'
import { RoutesManager } from 'utils/routing/routesManager'

export interface PurposeViewContextValues {
  isReady: boolean
  purpose: MaybeNull<PurposeDTO>
  organizationLanguages: LanguageWithStatusDTO[]
  versions: PurposeDTO[]
}

const { Provider, useCustomContext: usePurposeViewContext } = createCustomContext<PurposeViewContextValues>({
  displayName: 'PurposeViewContext',
})

export const PurposeViewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate()
  const { code } = useParams<{ code: string }>()
  const { queries } = useQueryParams<{ version?: string }>()
  const { version } = queries

  const handleRedirectToPurposesList = () => {
    navigate(RoutesManager.policyCenter.purposes.root.getURL())
  }

  const { data: purpose, isLoading: isPurposeLoading } = usePurpose({
    params: {
      purposeCode: code!,
      includeIssues: true,
      includeMetadata: true,
    },
    onError: handleRedirectToPurposesList,
  })

  const { data: purposeVersion, isLoading: isPurposeVersionLoading } = usePurposeVersion({
    enabled: !!version && !!purpose,
    params: {
      purposeCode: code!,
      version: version!,
    },
    onError: handleRedirectToPurposesList,
  })

  const { data: organizationLanguages, isLoading: isLanguagesListLoading } = useOrganizationLanguages()

  const { data: versions, isLoading: isVersionsLoading } = usePurposeVersions({
    enabled: !!purpose,
    params: {
      purposeCode: code!,
      includeMetadata: true,
      ordering: {
        version: 'desc',
      },
    },
  })

  const isReady =
    !isVersionsLoading && !isLanguagesListLoading && version ? !isPurposeVersionLoading : !isPurposeLoading

  const value = useMemo(
    () => ({
      isReady,
      purpose: version ? purposeVersion : purpose,
      organizationLanguages,
      versions,
    }),
    [isReady, purpose, organizationLanguages, versions, purposeVersion, version],
  )

  return <Provider value={value}>{children}</Provider>
}

export { usePurposeViewContext }
